import {
  Button,
  Card,
  CardBody,
  DefinitionList,
  DefinitionListItem,
  DefinitionTerm,
  DefinitionValue,
  LabeledDatePickerInput,
  SectionHeader,
} from "@apex/component-library";
import {
  Box,
  Icon,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Formik, FormikConfig } from "formik";
import { withFormikDevtools } from "formik-devtools-extension";
import { useState } from "react";
import { FaTimes } from "react-icons/fa";

import { useGetBalanceRollupDetails } from "../../../api/useGetBalanceRollupDetails";
import { useGetBalances } from "../../../api/useGetBalances";
import { useGetMargins } from "../../../api/useGetMargins";
import { useAccountLookupStore } from "../../../hooks/useAccountLookupStore";
import { useBalanceRollupStore } from "../../../hooks/useBalanceRollupStore";
import useProcessDateFormatted from "../../../hooks/useProcessDateFormatted";
import {
  getApiFormattedDate,
  getDatePickerFormattedDate,
} from "../../../utils/dateFormatter";
import { slugify } from "../../../utils/stringFormatters";
import SkeletonDefinitionList from "../../SkeletonDefinitionList";
import ToggleSwitchRolledUp from "../../ToggleSwitchRolledup";
import BalanceRollupTable from "./BalanceRollupTable";
import { BalancesTable } from "./BalancesTable";
import { processDateSchema, ProcessDateSearch } from "./processDateSchema";
import { buildBuyingPower } from "./utils/buildBuyingPower";
import { buildEquity } from "./utils/buildEquity";
import { buildRequirements } from "./utils/buildRequirements";

const HEIGHT_FOR_ERROR = "5.4rem";

function BuyingPowerTab() {
  const [submittingProcessDate, setSubmittingProcessDate] =
    useState<string>("");

  const [processDateInputValue, processDateDisplayValue, setProcessDate] =
    useProcessDateFormatted(recallAll);

  const { accountNumber } = useAccountLookupStore();
  const [isShowingRollup, isMasterSubAccount] = useBalanceRollupStore(state => [
    state.isShowingRollup,
    state.isMasterSubAccount,
  ]);

  const {
    data: balancesData,
    isLoading: balancesLoading,
    refetch: refetchBalances,
  } = useGetBalances(accountNumber, submittingProcessDate);

  const {
    data: rollupData,
    isLoading: isRollupLoading,
    refetch: refetchRollupData,
  } = useGetBalanceRollupDetails({
    accountNumber,
    processDate: submittingProcessDate,
  });

  const {
    data: marginData,
    isLoading: isMarginsLoading,
    refetch: refetchMargins,
  } = useGetMargins(accountNumber, submittingProcessDate);

  const buyingPower = marginData ? buildBuyingPower(marginData) : [];

  const equity = marginData ? buildEquity(marginData) : [];

  const requirements = marginData ? buildRequirements(marginData) : [];

  function recallAll(date?: string) {
    const dateForApi = date ? getApiFormattedDate(date) : ""; // avoid setting to current date by default
    setSubmittingProcessDate(dateForApi);
    refetchRollupData();
    refetchMargins();
    refetchBalances();
  }

  const onSubmit = (values: ProcessDateSearch) => {
    if (values.processDate) {
      setProcessDate(values.processDate);
    }
    recallAll(values.processDate);
  };

  const formikConfig: FormikConfig<ProcessDateSearch> = {
    initialValues: processDateSchema.cast({
      processDate: processDateInputValue,
    }) as ProcessDateSearch,
    validationSchema: processDateSchema,
    onSubmit,
    enableReinitialize: true,
  };

  return (
    <>
      <Formik {...formikConfig}>
        {formikProps => {
          withFormikDevtools(formikProps);

          return (
            <form onSubmit={formikProps.handleSubmit}>
              <Wrap
                bg="gray.100"
                border="1px solid"
                borderColor="gray.300"
                w="100%"
                padding={3}
                align="flex-end"
                spacing="1rem"
                width="100%"
                marginBottom="1rem"
                data-testid="balance-date-picker-wrap"
              >
                <WrapItem h={HEIGHT_FOR_ERROR}>
                  <Stack spacing="1.7px">
                    <LabeledDatePickerInput
                      label="Process Date"
                      name="processDate"
                      date={formikProps.values.processDate || ""}
                      disableFutureDates
                      data-testid="process-date-selector"
                      onChange={(date: string | Date) => {
                        formikProps.setFieldValue(
                          "processDate",
                          getDatePickerFormattedDate(date)
                        );
                      }}
                      onBlur={formikProps.handleBlur}
                      placement="bottom"
                      errorMessage={formikProps.errors.processDate}
                      isInvalid={
                        formikProps.touched.processDate &&
                        !!formikProps.errors.processDate
                      }
                    />
                    {!formikProps.errors.processDate &&
                      !!processDateDisplayValue && (
                        <Text color="gray.500" data-testid="end-of-day-date">
                          End of day {processDateDisplayValue}
                        </Text>
                      )}
                  </Stack>
                </WrapItem>
                <WrapItem
                  alignItems="center"
                  paddingTop="1rem"
                  h={HEIGHT_FOR_ERROR}
                >
                  <Stack direction="row" spacing={4} alignItems="center">
                    <Button
                      variant="primary"
                      type="submit"
                      isLoading={isMarginsLoading || isRollupLoading}
                      isDisabled={!formikProps.isValid}
                      data-testid="search-button"
                    >
                      Search
                    </Button>
                    <Button
                      variant="link"
                      isDisabled={formikProps.values.processDate === ""}
                      onClick={() => {
                        setProcessDate("");
                        formikProps.setFieldValue("processDate", "");
                      }}
                      data-testid="clear-button"
                    >
                      <Icon as={FaTimes} pr="5px" /> Clear
                    </Button>
                  </Stack>
                </WrapItem>
              </Wrap>
            </form>
          );
        }}
      </Formik>
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
        <Stack gap={6}>
          <Card>
            <SectionHeader>Buying Power</SectionHeader>
            <CardBody>
              {balancesLoading || isMarginsLoading ? (
                <Box py={2}>
                  <SkeletonDefinitionList count={12} />
                </Box>
              ) : (
                <DefinitionList>
                  {buyingPower.map(item => {
                    return (
                      <DefinitionListItem
                        key={item.label}
                        _last={{
                          borderBottomWidth: 0,
                        }}
                        data-testid={`definition-${slugify(
                          String(item.label)
                        )}`}
                      >
                        <DefinitionTerm>{item.label}</DefinitionTerm>
                        <DefinitionValue>
                          <Box w="50%" textAlign="right">
                            {item.value}
                          </Box>
                        </DefinitionValue>
                      </DefinitionListItem>
                    );
                  })}
                </DefinitionList>
              )}
            </CardBody>
          </Card>
          <Card>
            <SectionHeader tools={<ToggleSwitchRolledUp />}>
              <Box>Balances</Box>
            </SectionHeader>
            <CardBody>
              {balancesLoading || isMarginsLoading ? (
                <Box py={2}>
                  <SkeletonDefinitionList count={4} />
                </Box>
              ) : (
                <>
                  <TableContainer mt="-px">
                    <Table variant="simple" size="sm" marginTop="1">
                      {!isShowingRollup && balancesData && (
                        <BalancesTable balancesData={balancesData} />
                      )}
                      {isShowingRollup && isMasterSubAccount && rollupData && (
                        <BalanceRollupTable rollupData={rollupData} />
                      )}
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardBody>
          </Card>
        </Stack>
        <Stack gap={6}>
          <Card>
            <SectionHeader>Equity</SectionHeader>
            <CardBody>
              {balancesLoading || isMarginsLoading ? (
                <Box py={2}>
                  <SkeletonDefinitionList count={5} />
                </Box>
              ) : (
                <DefinitionList>
                  {equity.map(item => (
                    <DefinitionListItem
                      key={item.label}
                      _last={{
                        borderBottomWidth: 0,
                      }}
                      data-testid={`definition-${slugify(String(item.label))}`}
                    >
                      <DefinitionTerm>{item.label}</DefinitionTerm>
                      <DefinitionValue>
                        <Box w="50%" textAlign="right">
                          {item.value}
                        </Box>
                      </DefinitionValue>
                    </DefinitionListItem>
                  ))}
                </DefinitionList>
              )}
            </CardBody>
          </Card>
          <Card>
            <SectionHeader>Requirements</SectionHeader>
            <CardBody>
              {balancesLoading ? (
                <Box py={2}>
                  <SkeletonDefinitionList count={5} />
                </Box>
              ) : (
                <DefinitionList>
                  {requirements.map(item => (
                    <DefinitionListItem
                      key={item.label}
                      _last={{
                        borderBottomWidth: 0,
                      }}
                      data-testid={`definition-${slugify(String(item.label))}`}
                    >
                      <DefinitionTerm>{item.label}</DefinitionTerm>
                      <DefinitionValue>
                        <Box w="50%" textAlign="right">
                          {item.value}
                        </Box>
                      </DefinitionValue>
                    </DefinitionListItem>
                  ))}
                </DefinitionList>
              )}
            </CardBody>
          </Card>
        </Stack>
      </SimpleGrid>
    </>
  );
}

export default BuyingPowerTab;
